import { computed, action, observable } from 'mobx';
import { ViewController } from '@estee/elc-service';
import { NumberFormatter } from '@estee/elc-formatters';
import { LoyaltyRepository, offerRedemptionType } from '~repositories/LoyaltyRepository';
import { emit, events } from '@estee/elc-service-bus';
import { ILoyaltyRPCRequest } from '~api/interfaces/ILoyaltyRpcAPI';
import { diContainer } from '~setup/diContainer';
import serviceNames from '~setup/LoyaltyService';
import { IRewardsContent, IRewardsInfo } from '~views/dashboard/loyalty-rewards/ILoyaltyRewards';
import Rewards from '~domain/entities/LoyaltyRewardsInfo';
import { PriorityOrderSort, CategoryOrderSort } from '~utils/OffersSorting';

export interface IConfigParamsNumberFormats {
    groupingFormatForComma?: boolean;
    rewards: IRewardsContent;
}

class LoyaltyRewardsController extends ViewController {
    private loyaltyRepository: LoyaltyRepository;
    private numberFormatter: NumberFormatter;

    @observable currentTabIndex: string;
    @observable private configParams: IConfigParamsNumberFormats;
    @observable rewardOfferType: string;

    constructor(loyaltyRepository: LoyaltyRepository) {
        super();
        this.loyaltyRepository = loyaltyRepository;
        this.numberFormatter = diContainer.get(serviceNames.numberFormatter);
    }

    @action
    public setConfigParams = (configParams: IConfigParamsNumberFormats) => {
        this.configParams = configParams;
    };

    @action
    public loaded = async (rewardOfferType: string) => {
        this.isLoading = true;
        await this.loyaltyRepository.fetchRewardsData(rewardOfferType);
        this.rewardOfferType = rewardOfferType;
        this.isLoading = false;
    };

    public onShopNowLinkClick = (linkLabel: string) => {
        emit(events.LOYALTY_SHOP_NOW_CLICKED, linkLabel);
    };

    @action
    public handleRedeemOffer = async (applyOfferCode: ILoyaltyRPCRequest) => {
        this.isLoading = true;

        await this.loyaltyRepository.redeemOffer(
            applyOfferCode,
            this.rewardOfferType,
            offerRedemptionType.rewards
        );

        const rewardsMessageData = this.loyaltyRepository?.offerRedemptionMessageData?.rewards;
        const offerSuccessMessageFromConfig = this.configParams?.rewards?.offerSuccessMessage?.text;
        if (
            offerSuccessMessageFromConfig &&
            rewardsMessageData?.type === 'success' &&
            rewardsMessageData?.redemptionAction === 'applied'
        ) {
            this.loyaltyRepository.setOfferRedemptionMessage({
                ...rewardsMessageData,
                content: offerSuccessMessageFromConfig
            });
        }

        const { offerCode, offerView } = applyOfferCode;
        if (offerView === 'ChoicesOfSku') {
            const cartItemcCount = this.loyaltyRepository?.benefitsOfferInfo?.cartItemsCount;
            if (cartItemcCount) {
                emit(events.LOYALTY_REWARDS_REDEEM_CLICKED);
            }
        } else {
            const redeemOffer = this.loyaltyRepository?.benefitsOfferInfo?.inCartStatus;
            emit(events.LOYALTY_BENEFITS_REDEEM_CLICKED, offerCode);
            if (redeemOffer) {
                emit(events.LOYALTY_BENEFITS_REDEEM_CLICKED, offerCode);
            }
        }
        const cartItemQty = this.loyaltyRepository?.benefitsOfferInfo?.cartItemQty;
        const cartSubTotal = this.loyaltyRepository?.benefitsOfferInfo?.cartSubTotal;
        if (cartItemQty) {
            const eventPayload = {
                itemQty: cartItemQty,
                subtotal: cartSubTotal
              };
            const cartEvent = new CustomEvent(events.CART_UPDATED, {
                detail: eventPayload
              });
            document.dispatchEvent(cartEvent);
        }

        this.isLoading = false;
    };

    @action
    public handleRemoveOffer = async (offerCode: string, offerView: string) => {
        this.isLoading = true;

        await this.loyaltyRepository.removeOffer(
            offerCode,
            this.rewardOfferType,
            offerRedemptionType.rewards
        );

        const rewardsMessageData = this.loyaltyRepository?.offerRedemptionMessageData?.rewards;
        const offerRemovedMessageFromConfig = this.configParams?.rewards?.offerRemovedMessage?.text;
        if (
            offerRemovedMessageFromConfig &&
            rewardsMessageData?.type === 'success' &&
            rewardsMessageData?.redemptionAction === 'removed'
        ) {
            this.loyaltyRepository.setOfferRedemptionMessage({
                ...rewardsMessageData,
                content: offerRemovedMessageFromConfig
            });
        }

        if (offerView === 'ChoicesOfSku') {
            emit(events.LOYALTY_REWARDS_REMOVE_CLICKED);
        } else {
            emit(events.LOYALTY_BENEFITS_REMOVE_CLICKED, offerCode);
        }
        this.isLoading = false;
    };

    @action
    public setCurrentTabId = (tabId: string) => {
        this.currentTabIndex = tabId;

        emit(events.LOYALTY_REWARDS_TAB_CLICKED, tabId);
    };

    @computed
    get currentTabId() {
        return this.currentTabIndex;
    }

    @computed
    public get formattedPointsValue() {
        const { totalRedeemedPoints = 0 } = this.loyaltyRepository?.rewardsInfo || {};
        const pointsValue =
            this.loyaltyRepository?.loyaltyInfo?.currentPoints - totalRedeemedPoints;

        const { groupingFormatForComma } = this.configs;

        return this.numberFormatter.formatNumber(pointsValue, {
            shouldUseGrouping: groupingFormatForComma
        });
    }

    @action
    public formattedCurrentPoints(value: { text: string }, currentPoints: string) {
        return value?.text && currentPoints
            ? value.text.replace('::currentPoints::', currentPoints)
            : '';
    }

    @computed
    public get formattedPointsInfoDescription() {
        const { rewardsPointsInfo } = this.configs.rewards;

        return this.formattedCurrentPoints(rewardsPointsInfo, this.formattedPointsValue);
    }

    @computed
    public get shouldDisplayInsufficientPointsSubHeader() {
        const { tierLevels } = this.configs.rewards;
        const { totalRedeemedPoints = 0 } = this.loyaltyRepository?.rewardsInfo || {};
        const currentPoints = this.loyaltyRepository?.loyaltyInfo?.currentPoints - totalRedeemedPoints;

        if (tierLevels && tierLevels.length > 0) {
            return currentPoints < tierLevels[0].points;
        } else {
            return false;
        }
    }

    @computed
    public get formattedRewardsDescription() {
        const { rewardsSubHeader, rewardsSubHeaderInsufficientPoints } = this.configs.rewards;

        if (rewardsSubHeaderInsufficientPoints && this.shouldDisplayInsufficientPointsSubHeader) {
            return this.formattedCurrentPoints(rewardsSubHeaderInsufficientPoints, this.formattedPointsValue);
        } else {
            return this.formattedCurrentPoints(rewardsSubHeader, this.formattedPointsValue);
        }
    }

    @computed
    public get data() {
        let rewardsInfo: IRewardsInfo[] = [];
        if (this.configParams?.rewards.sorting?.enable && this.loyaltyRepository?.rewardsInfo?.rewardsDataFormatted) {
            const { priorityOrder, categoryOrder } = this.configParams.rewards.sorting;
            rewardsInfo = Rewards.getSortedRewardsInfo(this.loyaltyRepository.rewardsInfo.rewardsDataFormatted,
                this.loyaltyRepository.rewardsInfo.totalRedeemedPoints,
                priorityOrder as PriorityOrderSort[], categoryOrder as CategoryOrderSort[]).rewardsDataFormatted;
        } else {
            rewardsInfo = this.loyaltyRepository?.rewardsInfo?.rewardsDataFormatted;
        }

        return {
            rewardsInfo,
            rewardsOfferInfo: this.loyaltyRepository?.benefitsOfferInfo,
            birthdayRedeemed: this.loyaltyRepository?.isBirthdayRedeemed,
            offerRedemptionMessages: this.loyaltyRepository?.offerRedemptionMessageData,
            isLoading: this.isLoading
        };
    }

    @computed
    public get configs() {
        return this.configParams;
    }
}

export default LoyaltyRewardsController;
